import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { ChevronRight, Folder, Loader2, X } from "lucide-react";
import {
  COMPOSE_DESCRIPTION_MAX_LENGTH,
  composeExactSearchFields,
  type FileLibraryFolder,
} from "@/api/file_library";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { buildChildrenByParentId } from "@/lib/documentGroupTree";
import {
  composeExactSearch,
  fieldsFromComposeTerms,
  fileCenterFolderPath,
  folderCheckState,
  exactSearchScopeText,
  folderScopeLabels,
  hasPositiveExactTerms,
  UNGROUPED_SCOPE_LABEL,
  joinExactPhrases,
  joinExactWords,
  splitExactPhrases,
  splitExactWords,
  toggleFolderSelection,
  withFolderSubtrees,
  withoutFolderSubtree,
  type ExactSearchFields,
} from "@/lib/fileCenterExactSearch";

const BUILDER_ERROR = "Could not fill the boxes. Try again, or type the words yourself.";
const BUILDER_PLACEHOLDER =
  "Example: every agreement that mentions the community center, but not invoices";

const EMPTY_FOLDER_IDS: number[] = [];
const EMPTY_FOLDERS: FileLibraryFolder[] = [];

type FileCenterAdvancedSearchModalProps = {
  open: boolean;
  municipalityId?: number;
  initialFields: ExactSearchFields;
  folders?: FileLibraryFolder[];
  selectedFolderIds?: number[];
  onOpenChange: (open: boolean) => void;
  onSearch: (composed: string, folderIds: number[], includeUngrouped: boolean) => void;
  ungroupedCount?: number;
  includeUngrouped?: boolean;
};

const FIELDS: Array<{
  key: keyof ExactSearchFields;
  label: string;
  hint: string;
  placeholder: string;
  summaryLabel: string;
  commitOnSpace: boolean;
}> = [
  {
    key: "all",
    label: "All of these words",
    hint: "The file has to contain every word you type here, not just some of them.",
    placeholder: "Type a word",
    summaryLabel: "Must include every word",
    commitOnSpace: true,
  },
  {
    key: "phrase",
    label: "This exact word or phrase",
    hint: "The file has to contain these words together, in this exact order.",
    placeholder: "Type the exact phrase",
    summaryLabel: "Must include this phrase",
    commitOnSpace: false,
  },
  {
    key: "any",
    label: "Any of these words",
    hint: "The file only needs to contain one of the words you type here.",
    placeholder: "Type a word",
    summaryLabel: "Match any of",
    commitOnSpace: true,
  },
  {
    key: "none",
    label: "None of these words",
    hint: "Leave out files that contain any of the words you type here.",
    placeholder: "Type a word",
    summaryLabel: "Leave out files with",
    commitOnSpace: true,
  },
];

function tokensFromField(key: keyof ExactSearchFields, value: string): string[] {
  return key === "phrase" ? splitExactPhrases(value) : splitExactWords(value);
}

function fieldFromPills(key: keyof ExactSearchFields, tokens: string[], draft: string): string {
  const extra = draft.trim();
  const next = extra ? [...tokens, extra] : tokens;
  return key === "phrase" ? joinExactPhrases(next) : joinExactWords(next);
}

function summaryLines(fields: ExactSearchFields): string[] {
  return FIELDS.flatMap((field) => {
    const tokens = tokensFromField(field.key, fields[field.key]).filter(
      (token) => token.trim().length >= 3,
    );
    return tokens.length > 0 ? [`${field.summaryLabel}: ${tokens.join(" ")}`] : [];
  });
}

function hasShortTypedToken(fields: ExactSearchFields): boolean {
  return FIELDS.some((field) =>
    tokensFromField(field.key, fields[field.key]).some((token) => {
      const trimmed = token.trim();
      return trimmed.length > 0 && trimmed.length < 3;
    }),
  );
}

function commitToken(value: string, tokens: string[]): string[] {
  const trimmed = value.trim();
  if (!trimmed) return tokens;
  if (tokens.some((token) => token.toLowerCase() === trimmed.toLowerCase())) return tokens;
  return [...tokens, trimmed];
}

function WordPillInput({
  id,
  resetKey,
  initialTokens,
  commitOnSpace,
  placeholder,
  disabled = false,
  onChange,
}: {
  id: string;
  resetKey: string;
  initialTokens: string[];
  commitOnSpace: boolean;
  placeholder: string;
  disabled?: boolean;
  onChange: (tokens: string[], draft: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [tokens, setTokens] = useState<string[]>(initialTokens);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    setTokens(initialTokens);
    setDraft("");
    // Reset only when the modal opens with a new prefill. initialTokens is that snapshot.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const emit = (nextTokens: string[], nextDraft: string) => {
    setTokens(nextTokens);
    setDraft(nextDraft);
    onChange(nextTokens, nextDraft);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Tab" && draft.trim()) {
      event.preventDefault();
      emit(commitToken(draft, tokens), "");
      return;
    }
    if (event.key === "Enter") {
      if (draft.trim()) {
        event.preventDefault();
        emit(commitToken(draft, tokens), "");
      }
      return;
    }
    if (event.key === "Backspace" && !draft && tokens.length > 0) {
      event.preventDefault();
      emit(tokens.slice(0, -1), "");
      return;
    }
    if (commitOnSpace && event.key === " " && draft.trim()) {
      event.preventDefault();
      emit(commitToken(draft, tokens), "");
    }
  };

  return (
    <div
      className={`flex min-h-9 w-full flex-wrap items-center gap-1.5 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-within:outline-none focus-within:ring-1 focus-within:ring-ring ${
        disabled ? "cursor-not-allowed opacity-60" : "cursor-text"
      }`}
      onClick={() => {
        if (!disabled) inputRef.current?.focus();
      }}
    >
      {tokens.map((token, index) => (
        <Badge key={`${token}-${index}`} variant="secondary" className="gap-1 pr-1 font-normal">
          {token}
          <button
            type="button"
            className="rounded-sm opacity-70 hover:opacity-100"
            aria-label={`Remove ${token}`}
            disabled={disabled}
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              if (disabled) return;
              event.stopPropagation();
              emit(
                tokens.filter((_, itemIndex) => itemIndex !== index),
                draft,
              );
            }}
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}
      <input
        id={id}
        ref={inputRef}
        className="min-w-[8ch] flex-1 border-0 bg-transparent p-0 text-sm outline-none placeholder:text-muted-foreground focus-visible:ring-0"
        value={draft}
        placeholder={placeholder}
        disabled={disabled}
        onChange={(event) => {
          if (disabled) return;
          const raw = event.target.value;
          if (!commitOnSpace || !/\s/.test(raw)) {
            emit(tokens, raw);
            return;
          }
          const parts = raw.split(/\s+/);
          const nextDraft = parts.pop() ?? "";
          let nextTokens = tokens;
          for (const part of parts) {
            nextTokens = commitToken(part, nextTokens);
          }
          emit(nextTokens, nextDraft);
        }}
        onKeyDown={(event) => {
          if (disabled) return;
          onKeyDown(event);
        }}
        onBlur={() => {
          if (disabled) return;
          emit(commitToken(draft, tokens), "");
        }}
        autoComplete="off"
      />
    </div>
  );
}

function FolderScopeBranch({
  folder,
  childrenByParent,
  folderIds,
  folders,
  visibleIds,
  openIds,
  disabled,
  onToggle,
  onOpenChange,
}: {
  folder: FileLibraryFolder;
  childrenByParent: Map<number | null, FileLibraryFolder[]>;
  folderIds: number[];
  folders: readonly FileLibraryFolder[];
  visibleIds: Set<number> | null;
  openIds: ReadonlySet<number>;
  disabled: boolean;
  onToggle: (folderId: number) => void;
  onOpenChange: (folderId: number, open: boolean) => void;
}) {
  const childFolders = (childrenByParent.get(folder.id) ?? []).filter(
    (child) => visibleIds == null || visibleIds.has(child.id),
  );
  const open = openIds.has(folder.id) || visibleIds != null;
  const state = folderCheckState(folder.id, folderIds, folders);
  const hasChildren = childFolders.length > 0 || folder.has_children;

  return (
    <div className="min-w-0">
      <div className="flex min-w-0 items-center gap-1">
        {hasChildren ? (
          <button
            type="button"
            className="rounded-sm p-0.5 text-muted-foreground hover:bg-muted"
            aria-label={`${open ? "Collapse" : "Expand"} ${folder.name}`}
            aria-expanded={open}
            disabled={disabled}
            onClick={() => {
              if (visibleIds != null) return;
              onOpenChange(folder.id, !openIds.has(folder.id));
            }}
          >
            <ChevronRight className={`h-4 w-4 transition-transform ${open ? "rotate-90" : ""}`} aria-hidden />
          </button>
        ) : (
          <span className="w-5 shrink-0" aria-hidden />
        )}
        <label className="flex min-w-0 flex-1 items-center gap-2 py-0.5 text-sm">
          <input
            type="checkbox"
            className="shrink-0"
            checked={state === "checked"}
            ref={(node) => {
              if (node) node.indeterminate = state === "partial";
            }}
            aria-checked={state === "partial" ? "mixed" : state === "checked"}
            disabled={disabled}
            onChange={() => onToggle(folder.id)}
          />
          <Folder className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="truncate">{folder.name}</span>
        </label>
      </div>
      {open && childFolders.length > 0 ? (
        <div className="ml-2.5 space-y-0.5 border-l border-border/70 pl-2">
          {childFolders.map((child) => (
            <FolderScopeBranch
              key={child.id}
              folder={child}
              childrenByParent={childrenByParent}
              folderIds={folderIds}
              folders={folders}
              visibleIds={visibleIds}
              openIds={openIds}
              disabled={disabled}
              onToggle={onToggle}
              onOpenChange={onOpenChange}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function folderAncestorIds(
  folderIds: readonly number[],
  folders: readonly FileLibraryFolder[],
): Set<number> {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const ancestors = new Set<number>();
  for (const id of folderIds) {
    const parentId = byId.get(id)?.parent_id ?? null;
    if (parentId == null) continue;
    if (folderCheckState(parentId, folderIds, folders) === "checked") continue;
    let cursor: number | null = parentId;
    const seen = new Set<number>();
    while (cursor != null && !seen.has(cursor)) {
      seen.add(cursor);
      ancestors.add(cursor);
      cursor = byId.get(cursor)?.parent_id ?? null;
    }
  }
  return ancestors;
}

function FolderScopeField({
  folders,
  folderIds,
  initialFolderIds,
  filter,
  disabled,
  includeUngrouped,
  showUngrouped,
  onFilterChange,
  onFolderIdsChange,
  onIncludeUngroupedChange,
}: {
  folders: readonly FileLibraryFolder[];
  folderIds: number[];
  initialFolderIds: readonly number[];
  filter: string;
  disabled: boolean;
  includeUngrouped: boolean;
  showUngrouped: boolean;
  onFilterChange: (value: string) => void;
  onFolderIdsChange: (folderIds: number[]) => void;
  onIncludeUngroupedChange: (includeUngrouped: boolean) => void;
}) {
  const [openIds, setOpenIds] = useState<Set<number>>(() => folderAncestorIds(initialFolderIds, folders));
  const folderDataWasEmptyRef = useRef(folders.length === 0);
  const needle = filter.trim().toLowerCase();
  const childrenByParent = useMemo(() => buildChildrenByParentId(folders), [folders]);

  useEffect(() => {
    if (!folderDataWasEmptyRef.current || folders.length === 0) return;
    const ancestors = folderAncestorIds(folderIds, folders);
    setOpenIds((current) => {
      if (ancestors.size === 0) return current;
      const next = new Set(current);
      ancestors.forEach((id) => next.add(id));
      return next;
    });
    folderDataWasEmptyRef.current = false;
  }, [folders, folderIds]);
  const visibleIds = useMemo(() => {
    if (needle.length === 0) return null;
    const ids = new Set<number>();
    const byId = new Map(folders.map((folder) => [folder.id, folder]));
    for (const folder of folders) {
      const path = fileCenterFolderPath(folder.id, folders).toLowerCase();
      if (!path.includes(needle) && !folder.name.toLowerCase().includes(needle)) continue;
      ids.add(folder.id);
      let parentId = folder.parent_id;
      const seen = new Set<number>();
      while (parentId != null && !seen.has(parentId)) {
        seen.add(parentId);
        ids.add(parentId);
        parentId = byId.get(parentId)?.parent_id ?? null;
      }
    }
    return ids;
  }, [folders, needle]);
  const scopeLabels = folderScopeLabels(folderIds, folders);
  const ungroupedVisible = showUngrouped && (needle.length === 0 || UNGROUPED_SCOPE_LABEL.toLowerCase().includes(needle));

  const toggle = (folderId: number) => {
    onFolderIdsChange(toggleFolderSelection(folderIds, folderId, folders));
  };

  const roots = (childrenByParent.get(null) ?? []).filter(
    (folder) => visibleIds == null || visibleIds.has(folder.id),
  );

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p id="file-center-exact-folders-label" className="text-sm font-medium">
          Folders
        </p>
        <div className="flex items-center gap-3">
          {folders.length > 0 ? (
            <button
              type="button"
              className="text-xs font-medium text-primary hover:underline"
              disabled={disabled}
              onClick={() => onFolderIdsChange(folders.map((folder) => folder.id))}
            >
              Select all folders
            </button>
          ) : null}
          {folderIds.length > 0 || includeUngrouped ? (
            <button
              type="button"
              className="text-xs font-medium text-primary hover:underline"
              disabled={disabled}
              onClick={() => {
                onFolderIdsChange([]);
                onIncludeUngroupedChange(false);
              }}
            >
              Clear folders
            </button>
          ) : null}
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        Checking a folder searches that folder and the folders inside it. Uncheck a folder inside
        it to leave that one out. Leave this empty to search the whole library.
      </p>
      {scopeLabels.length > 0 || includeUngrouped ? (
        <div className="flex flex-wrap gap-1.5" aria-label="Selected folders">
          {scopeLabels.map((entry) => (
            <Badge key={`${entry.folderId}:${entry.label}`} variant="secondary" className="gap-1 pr-1 font-normal">
              {entry.label}
              <button
                type="button"
                className="rounded-sm opacity-70 hover:opacity-100"
                aria-label={`Remove ${entry.label}`}
                disabled={disabled}
                onClick={() => onFolderIdsChange(withoutFolderSubtree(folderIds, entry.folderId, folders))}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          {includeUngrouped ? (
            <Badge variant="secondary" className="gap-1 pr-1 font-normal">
              {UNGROUPED_SCOPE_LABEL}
              <button
                type="button"
                className="rounded-sm opacity-70 hover:opacity-100"
                aria-label={`Remove ${UNGROUPED_SCOPE_LABEL}`}
                disabled={disabled}
                onClick={() => onIncludeUngroupedChange(false)}
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ) : null}
        </div>
      ) : null}
      {folders.length > 0 || showUngrouped ? (
        <>
          <input
            aria-label="Find a folder"
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm outline-none placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-ring"
            value={filter}
            placeholder="Find a folder"
            disabled={disabled}
            onChange={(event) => onFilterChange(event.target.value)}
          />
          <div
            role="group"
            aria-labelledby="file-center-exact-folders-label"
            className="max-h-52 space-y-0.5 overflow-y-auto rounded-md border px-2 py-2"
          >
            {roots.map((folder) => (
              <FolderScopeBranch
                key={folder.id}
                folder={folder}
                childrenByParent={childrenByParent}
                folderIds={folderIds}
                folders={folders}
                visibleIds={visibleIds}
                openIds={openIds}
                disabled={disabled}
                onToggle={toggle}
                onOpenChange={(folderId, open) => {
                  setOpenIds((current) => {
                    const next = new Set(current);
                    if (open) next.add(folderId);
                    else next.delete(folderId);
                    return next;
                  });
                }}
              />
            ))}
            {roots.length === 0 && !ungroupedVisible ? (
              <p className="px-1 text-sm text-muted-foreground">No folders match.</p>
            ) : null}
            {ungroupedVisible ? (
              <label className="flex min-w-0 items-center gap-2 py-0.5 pl-6 text-sm">
                <input
                  type="checkbox"
                  className="shrink-0"
                  checked={includeUngrouped}
                  disabled={disabled}
                  onChange={() => onIncludeUngroupedChange(!includeUngrouped)}
                />
                <Folder className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <span className="truncate">{UNGROUPED_SCOPE_LABEL}</span>
              </label>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

export function FileCenterAdvancedSearchModal({
  open,
  municipalityId,
  initialFields,
  folders = EMPTY_FOLDERS,
  selectedFolderIds = EMPTY_FOLDER_IDS,
  ungroupedCount = 0,
  includeUngrouped: includeUngroupedProp = false,
  onOpenChange,
  onSearch,
}: FileCenterAdvancedSearchModalProps) {
  const [fields, setFields] = useState<ExactSearchFields>(initialFields);
  const [folderIds, setFolderIds] = useState<number[]>(selectedFolderIds);
  const [includeUngrouped, setIncludeUngrouped] = useState(includeUngroupedProp);
  const [folderFilter, setFolderFilter] = useState("");
  const [pillNonce, setPillNonce] = useState(0);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [builderText, setBuilderText] = useState("");
  const [builderBusy, setBuilderBusy] = useState(false);
  const [builderError, setBuilderError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const municipalityIdRef = useRef(municipalityId);
  municipalityIdRef.current = municipalityId;
  const resetKey = open ? `open:${pillNonce}` : "closed";
  const composed = composeExactSearch(fields);
  const preview = summaryLines(fields);
  const canSearch = hasPositiveExactTerms(fields) && !builderBusy;

  const showPills = (next: ExactSearchFields) => {
    setFields(next);
    setPillNonce((n) => n + 1);
  };

  useEffect(() => {
    if (!open) {
      abortRef.current?.abort();
      return;
    }
    setFields(initialFields);
    setFolderIds(selectedFolderIds);
    setIncludeUngrouped(includeUngroupedProp);
    setFolderFilter("");
    setPillNonce((n) => n + 1);
    setBuilderOpen(false);
    setBuilderText("");
    setBuilderBusy(false);
    setBuilderError(null);
    return () => abortRef.current?.abort();
  }, [open, initialFields, selectedFolderIds, includeUngroupedProp]);

  useEffect(() => {
    abortRef.current?.abort();
    setBuilderBusy(false);
    setBuilderError(null);
  }, [municipalityId]);

  const setFromPills = (key: keyof ExactSearchFields, tokens: string[], draft: string) => {
    setFields((prev) => ({ ...prev, [key]: fieldFromPills(key, tokens, draft) }));
  };

  const toggleBuilder = () => {
    if (builderBusy) return;
    if (builderOpen) {
      setBuilderOpen(false);
      setBuilderError(null);
      return;
    }
    setBuilderOpen(true);
  };

  const submitBuilder = async () => {
    const description = builderText.trim();
    const requestMunicipalityId = municipalityId;
    if (!requestMunicipalityId || !description || builderBusy) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setBuilderBusy(true);
    setBuilderError(null);
    try {
      const terms = await composeExactSearchFields(
        requestMunicipalityId,
        description,
        controller.signal,
      );
      if (controller.signal.aborted || municipalityIdRef.current !== requestMunicipalityId) return;
      if (!hasPositiveExactTerms(fieldsFromComposeTerms(terms))) {
        setBuilderError(BUILDER_ERROR);
        return;
      }
      if (terms.folders.length > 0) {
        setFolderIds(withFolderSubtrees(terms.folders.map((folder) => folder.id), folders));
      }
      showPills(fieldsFromComposeTerms(terms));
      setBuilderOpen(false);
      setBuilderText("");
    } catch (error) {
      if (controller.signal.aborted || municipalityIdRef.current !== requestMunicipalityId) return;
      const message = error instanceof Error && error.message ? error.message : BUILDER_ERROR;
      setBuilderError(message);
    } finally {
      if (!controller.signal.aborted && municipalityIdRef.current === requestMunicipalityId) {
        setBuilderBusy(false);
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Advanced Search</DialogTitle>
          <DialogDescription className="text-left">
            Find files by the words inside them, not just the file name. Fill in only the boxes you
            need.
          </DialogDescription>
        </DialogHeader>

        <form
          className="space-y-5"
          onSubmit={(event) => {
            event.preventDefault();
            if (!canSearch) return;
            onSearch(composed, folderIds, includeUngrouped);
            onOpenChange(false);
          }}
        >
          <div className="grid grid-cols-[9.5rem_1fr] items-start gap-x-3 gap-y-4">
            {FIELDS.map((field) => {
              const id = `file-center-advanced-${field.key}`;
              return (
                <div key={field.key} className="contents">
                  <Label htmlFor={id} className="pt-2 leading-snug">
                    {field.label}
                  </Label>
                  <div className="space-y-1">
                    <WordPillInput
                      id={id}
                      resetKey={`${resetKey}:${field.key}`}
                      initialTokens={tokensFromField(field.key, fields[field.key])}
                      commitOnSpace={field.commitOnSpace}
                      placeholder={field.placeholder}
                      disabled={builderBusy}
                      onChange={(tokens, draft) => setFromPills(field.key, tokens, draft)}
                    />
                    <p className="text-xs text-muted-foreground">{field.hint}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {folders.length > 0 || folderIds.length > 0 || ungroupedCount > 0 || includeUngrouped ? (
            <FolderScopeField
              key={open ? "open" : "closed"}
              folders={folders}
              folderIds={folderIds}
              initialFolderIds={selectedFolderIds}
              filter={folderFilter}
              disabled={builderBusy}
              includeUngrouped={includeUngrouped}
              showUngrouped={ungroupedCount > 0 || includeUngrouped}
              onFilterChange={setFolderFilter}
              onFolderIdsChange={setFolderIds}
              onIncludeUngroupedChange={setIncludeUngrouped}
            />
          ) : null}

          {builderOpen ? (
            <div
              className="space-y-3 rounded-lg border bg-muted/40 p-4"
              role="region"
              aria-labelledby="file-center-ai-query-builder-heading"
              aria-busy={builderBusy}
            >
              <h3 id="file-center-ai-query-builder-heading" className="text-base font-semibold">
                Describe your search
              </h3>
              <p className="text-sm text-muted-foreground">
                Describe the files you need. The search fields above will be intelligently
                completed from that description.
              </p>
              {builderBusy ? (
                <div className="flex justify-center py-2" role="status" aria-label="Loading">
                  <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
                </div>
              ) : null}
              {builderError ? (
                <p role="alert" className="text-sm text-destructive">{builderError}</p>
              ) : null}
              <Textarea
                aria-label="Request in your own words"
                value={builderText}
                onChange={(event) => setBuilderText(event.target.value)}
                placeholder={BUILDER_PLACEHOLDER}
                disabled={builderBusy}
                maxLength={COMPOSE_DESCRIPTION_MAX_LENGTH}
                rows={4}
              />
              <Button
                type="button"
                onClick={() => void submitBuilder()}
                disabled={builderBusy || builderText.trim().length === 0}
              >
                Submit
              </Button>
            </div>
          ) : null}

          <div className="space-y-1.5" aria-live="polite" aria-atomic="true">
            <p className="text-sm font-medium">Your search</p>
            {preview.length > 0 || folderIds.length > 0 || includeUngrouped ? (
              <ul className="space-y-1 rounded-md border bg-muted/40 px-3 py-2 text-sm">
                {folderIds.length > 0 || includeUngrouped ? (
                  <li>Searching in: {exactSearchScopeText(folderIds, folders, includeUngrouped)}</li>
                ) : null}
                {preview.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            ) : (
              <p className="rounded-md border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                Nothing yet. Type in a box above.
              </p>
            )}
            {hasShortTypedToken(fields) ? (
              <p className="text-xs text-muted-foreground">
                Each word needs at least three characters.
              </p>
            ) : !hasPositiveExactTerms(fields) && fields.none.trim().length > 0 ? (
              <p className="text-xs text-muted-foreground">
                Add at least one word to look for. Leaving words out is optional.
              </p>
            ) : null}
          </div>

          <DialogFooter className="sm:justify-between">
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              {municipalityId != null ? (
                <Button
                  type="button"
                  variant="outline"
                  className="border-blue-200 bg-blue-50 text-blue-800 hover:bg-blue-100 hover:text-blue-900"
                  aria-expanded={builderOpen}
                  onClick={toggleBuilder}
                  disabled={builderBusy}
                >
                  AI Query Builder
                </Button>
              ) : null}
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
            </div>
            <Button type="submit" disabled={!canSearch}>
              Search
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
